import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useExpanded } from "react-table";
import Loading from "components/Loading";
import "./index.scss";

const Table = ({
  columns,
  data,
  subComponent,
  fetchData,
  pageCount: controlledPageCount,
  loading,
  isPaginated,
  className,
  onRowClick,
  onHeaderClick,
  isSorted,
  setSortOrder,
  sortValue,
}) => {
  const stateReducer = useCallback((newState, action, prevState) => {
    if (action.type === "toggleRowExpanded") {
      const prevExpanded = Object.keys(prevState.expanded);
      const newExpanded = Object.keys(newState.expanded);
      if (newExpanded.length > 1) {
        const nextExpanded = {};
        const filtered = newExpanded.filter(
          (item) => item !== prevExpanded[0]
        )[0];
        nextExpanded[filtered] = true;

        return { ...newState, expanded: nextExpanded };
      }
    }
    return newState;
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      stateReducer,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      manualPagination: true,
      autoResetPage: false,
      pageCount: controlledPageCount,
    },
    useExpanded,
    usePagination
  );

  useEffect(() => {
    if (fetchData) {
      fetchData({ pageIndex, pageSize });
    }

    return null;
  }, [fetchData, pageIndex, pageSize]);

  useEffect(() => {
    if (pageCount < pageIndex + 1) {
      gotoPage(pageCount - 1);
    }
  }, [gotoPage, pageCount, pageIndex]);

  const showSortIcon = () =>
    isSorted ? (
      <span
        className="icon-sort-up"
        data-testid="ascending"
        onClick={() => {
          setSortOrder("desc");
        }}
        aria-hidden
      />
    ) : (
      <span
        className="icon-sort-down"
        data-testid="descending"
        onClick={() => {
          setSortOrder("asc");
        }}
        aria-hidden
      />
    );

  return (
    <>
      <table data-testid="table" {...getTableProps()} className="table">
        <thead className="table-head">
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              key={headerGroup}
              className={`table-row ${className}`}
            >
              {headerGroup.headers.map((column) => {
                const col = column?.id.split(".");
                return (
                  <th
                    key={column}
                    className="table-header"
                    onClick={(event) => {
                      if (!onHeaderClick) return;
                      onHeaderClick(event, column);
                    }}
                  >
                    {column.render("Header")}
                    {col[1] === sortValue ? showSortIcon() : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="table-body">
          {loading ? (
            <tr data-testid="loading">
              <td colSpan={4} style={{ textAlign: "center" }}>
                <Loading uniqueKey="farms-and-fields" />
              </td>
            </tr>
          ) : (
            page.map((row) => {
              prepareRow(row);

              return (
                <React.Fragment key={row.id}>
                  <tr
                    {...row.getRowProps()}
                    className={`table-row ${className}`}
                    style={{ cursor: onRowClick ? "pointer" : "default" }}
                    onClick={(event) => {
                      if (!onRowClick) return;

                      onRowClick(event, row);
                    }}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()} className="table-data">
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {row.isExpanded ? subComponent : null}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {isPaginated && (
        <div className="pagination-wrapper">
          <div className="pagination-size">
            Show
            <select
              className="select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[5, 10, 15, 20].map((newPageSize) => (
                <option key={newPageSize} value={newPageSize}>
                  {newPageSize}
                </option>
              ))}
            </select>
            Items
          </div>

          <div className="main-container">
            <button
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              type="button"
              className="first-page"
            >
              First
            </button>
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              type="button"
              className="previous-page"
            >
              <span className="icon-chevron-left" />
              Prev
            </button>
            <span>
              Page{" "}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{" "}
            </span>
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              type="button"
              className="next-page"
            >
              Next
              <span className="icon-chevron-right" />
            </button>
            <button
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              type="button"
              className="last-page"
            >
              Last
            </button>
          </div>
          <div className="page-number">
            Go to page:{" "}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const targetPage = e.target.value
                  ? Number(e.target.value) - 1
                  : 0;
                gotoPage(targetPage);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      accessor: PropTypes.string.isRequired,
      Cell: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  subComponent: PropTypes.element,
  fetchData: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  isPaginated: PropTypes.bool,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  onHeaderClick: PropTypes.func,
  isSorted: PropTypes.bool,
  setSortOrder: PropTypes.func,
  sortValue: PropTypes.string,
};

Table.defaultProps = {
  subComponent: null,
  isPaginated: true,
  className: "",
  onRowClick: null,
  onHeaderClick: null,
  isSorted: false,
  setSortOrder: null,
  sortValue: "",
};

export default Table;
